import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/* Routes are code-split, so on a direct hit to /life/adventures#hiking the
   target does not exist yet when the browser resolves the hash — it gives up
   and leaves you at the top. Retry across a few frames until the lazy chunk
   has mounted, then stop. */
const HASH_RETRY_MS = 1500;

const scrollToHash = (hash, done) => {
  const id = decodeURIComponent(hash.slice(1));
  const start = performance.now();

  const attempt = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ block: 'start' });
      done?.(el);
      return;
    }
    if (performance.now() - start < HASH_RETRY_MS) requestAnimationFrame(attempt);
  };

  requestAnimationFrame(attempt);
};

/**
 * Replaces the old ScrollToTop, which scrolled but left focus on the link
 * the user had just clicked and never told a screen reader that anything
 * had happened.
 *
 * On every navigation: resolve the hash (or scroll to top), move focus so
 * the next Tab starts at the top of the new page, and announce the new
 * document title politely.
 */
const RouteChange = () => {
  const { pathname, hash } = useLocation();
  const [message, setMessage] = useState('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (hash) {
      scrollToHash(hash);
    } else {
      window.scrollTo(0, 0);
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    // Sending focus to the top of the page would undo a hash jump, so
    // only do it when there is no hash target to preserve.
    if (!hash) {
      const main = document.getElementById('main');
      if (main) {
        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
    }

    // Titles are set by usePageHead in the same commit; read on the next
    // tick so the announcement matches the page that actually rendered.
    const timer = setTimeout(() => setMessage(document.title), 120);
    return () => clearTimeout(timer);
  }, [pathname, hash]);

  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
};

export default RouteChange;
